import Keycloak from "keycloak-js";
import {
  KEYCLOAK_URL,
  KEYCLOAK_REALM,
  KEYCLOAK_CLIENT_ID,
  KEYCLOAK_REDIRECT_URI,
} from "@/consts";
import setPostLoginURL from "@/helpers/auth/setPostLoginURL";

export interface KeycloakSession {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  identity: string;
  isCallback: boolean;
}

export interface KeycloakLogoutTokens {
  accessToken?: string | null;
  refreshToken?: string | null;
  idToken?: string | null;
}

export type KeycloakLoginResult =
  | { authenticated: false }
  | { authenticated: true; session: KeycloakSession };

export const createKeycloak = (): Keycloak =>
  new Keycloak({
    url: KEYCLOAK_URL,
    realm: KEYCLOAK_REALM,
    clientId: KEYCLOAK_CLIENT_ID,
  });

export const loginWithKeycloak = async (): Promise<KeycloakLoginResult> => {
  const destination = new URL(window.location.href);
  const isCallback =
    destination.pathname === new URL(KEYCLOAK_REDIRECT_URI).pathname;
  // Complete sign-ins started by the previous query-mode callback flow.
  const legacyCallback = isCallback && destination.searchParams.has("state");
  const keycloak = createKeycloak();

  const authenticated = await keycloak.init({
    redirectUri: KEYCLOAK_REDIRECT_URI,
    responseMode: legacyCallback ? "query" : "fragment",
    flow: "standard",
    pkceMethod: "S256",
    useNonce: true,
    checkLoginIframe: false,
    enableLogging: true,
    scope: "openid",
  });

  if (!authenticated) {
    if (!isCallback) {
      setPostLoginURL();
    }
    await keycloak.login({
      scope: "openid",
      idpHint:
        destination.searchParams.get("kc_idp_hint") === "ibg-test"
          ? "ibg-test"
          : undefined,
    });
    return { authenticated: false };
  }

  const subject = keycloak.subject;
  const issuer = keycloak.idTokenParsed?.iss;
  if (!keycloak.token || !subject || !issuer) {
    throw new Error("Keycloak did not return a valid authenticated identity");
  }

  return {
    authenticated: true,
    session: {
      accessToken: keycloak.token,
      refreshToken: keycloak.refreshToken,
      idToken: keycloak.idToken,
      identity: `${issuer}:${subject}`,
      isCallback,
    },
  };
};

export const logoutFromKeycloak = async (
  tokens: KeycloakLogoutTokens,
  redirectUri?: string,
): Promise<void> => {
  const keycloak = createKeycloak();
  await keycloak.init({
    checkLoginIframe: false,
    token: tokens.accessToken || undefined,
    refreshToken: tokens.refreshToken || undefined,
    idToken: tokens.idToken || undefined,
  });
  await keycloak.logout({ redirectUri });
};
